import { useMemo } from "react";

import type { QueryObserverResult } from "@tanstack/react-query";

import {
  useGetCurrencyCloudAccount,
  useGetCurrencyCloudStatus,
  useRequest,
} from "~/v2/api/hooks";
import { type Account, type Bank, getBanks } from "~/v2/api/rest/account";
import { getAchRelationships, getBankInstitution } from "~/v2/api/rest/banking";
import type {
  GetCurrencyCloudAccountResponse,
  GetCurrencyCloudStatusResponse,
} from "~/v2/api/rest/currencyCloud";
import { getCountryInfos } from "~/v2/api/rest/onboarding";
import type { AchRelationshipWithBankInfo } from "../../pages/dashboard/funding/common/types";
import { queryKeys } from "../queryKeys";

type UseGetAllRelationshipsProps = {
  account: Account | undefined;
  countryOfTaxResidence: string;
  enabled?: boolean;
};

export type UseGetAllRelationshipsReturn = {
  ach: {
    relationships: AchRelationshipWithBankInfo[];
    isLoading: boolean;
    refetch: () => Promise<
      QueryObserverResult<AchRelationshipWithBankInfo[], unknown>
    >;
  };
  wire: {
    relationships: Bank[];
    isLoading: boolean;
    refetch: () => Promise<QueryObserverResult<Bank[], unknown>>;
  };
  currencyCloud: {
    account: GetCurrencyCloudAccountResponse | undefined;
    status: GetCurrencyCloudStatusResponse | undefined;
    isLoading: boolean;
    isEnabled: boolean;
    refetchAccount: () => Promise<
      QueryObserverResult<GetCurrencyCloudAccountResponse, unknown>
    >;
    refetchStatus: () => Promise<
      QueryObserverResult<GetCurrencyCloudStatusResponse, unknown>
    >;
  };
};

export const useGetAllRelationships = ({
  account,
  countryOfTaxResidence,
  enabled = true,
}: UseGetAllRelationshipsProps): UseGetAllRelationshipsReturn => {
  const accountID = account?.id ?? "";

  const ach = useRequest(
    queryKeys.funding.allRelationships.ach(accountID),
    async () => {
      const relationships = await getAchRelationships(accountID);

      // All this just for bank logos >:(
      return Promise.all(
        (relationships ?? []).map(async (relationship) => {
          // if no plaid_institution use generic bank logo
          if (!relationship.plaid_institution) {
            return {
              ...relationship,
              logo: "resources/images/bank.svg",
              brand_name: relationship.account_name ?? "",
            };
          }

          try {
            const bankInstitution = await getBankInstitution(
              relationship.plaid_institution,
            );

            const bankRelationship: AchRelationshipWithBankInfo = {
              ...bankInstitution,
              ...relationship,
              logo: `data:image/png;base64,${bankInstitution.logo}`,
            };

            return bankRelationship;
          } catch (error) {
            console.error("Failed to get bank institution", error);
            return {
              ...relationship,
              logo: "resources/images/bank.svg",
              brand_name: relationship.account_name ?? "",
            };
          }
        }),
      );
    },
    {
      enabled: enabled && !!accountID,
      refetchOnMount: "always",
    },
  );

  const banks = useRequest(
    queryKeys.funding.allRelationships.banks(accountID),
    () => getBanks({ accountID }),
    {
      enabled: enabled && !!accountID,
      refetchOnMount: "always",
    },
  );

  const { data: countryInfo } = useRequest(queryKeys.countryInfos(), () =>
    getCountryInfos(),
  );

  const isCurrencyCloudEnabled = useMemo(
    () => countryInfo?.[countryOfTaxResidence]?.currencycloud_enabled ?? false,
    [countryInfo, countryOfTaxResidence],
  );

  const ccStatus = useGetCurrencyCloudStatus({
    accountID,
    options: {
      enabled: enabled && !!accountID && isCurrencyCloudEnabled,
      refetchOnMount: "always",
    },
  });

  const ccAccount = useGetCurrencyCloudAccount({
    accountID,
    options: {
      enabled:
        enabled &&
        !!accountID &&
        isCurrencyCloudEnabled &&
        // returns 500 server error when beneficiary does not exist
        ccStatus.data?.beneficiary_exists === "true",
      refetchOnMount: "always",
    },
  });

  return {
    ach: {
      relationships: ach.data || [],
      isLoading: ach.isLoading,
      refetch: ach.refetch,
    },
    wire: {
      relationships: banks.data || [],
      isLoading: banks.isLoading,
      refetch: banks.refetch,
    },
    currencyCloud: {
      account: ccAccount.data,
      status: ccStatus.data,
      isLoading: ccAccount.isLoading || ccStatus.isLoading,
      isEnabled: enabled && isCurrencyCloudEnabled,
      refetchAccount: ccAccount.refetch,
      refetchStatus: ccStatus.refetch,
    },
  };
};
